<template>
  <b-card class="playlist-card rounded" :class="{ 'border-success': playlist.isActive }">
    <template #header>
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <div class="d-flex align-items-center gap-3">
          <i class="fas fa-list-ul text-primary" style="font-size: 1.2rem;"></i>
          <h5 class="mb-0 font-weight-bold">{{ playlist.title }}</h5>
          <span class="playlist-status" :class="playlist.isActive ? 'active' : 'inactive'">
            {{ playlist.isActive ? 'فعال' : 'غیرفعال' }}
          </span>
        </div>
        <div class="d-flex gap-2 ml-2">
          <b-button size="sm" variant="outline-primary" @click="$emit('add-item', playlist)">
            <feather-icon icon="PlusIcon"></feather-icon>

          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="$emit('edit', playlist)">
            <feather-icon icon="EditIcon"></feather-icon>
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="$emit('delete', playlist)">
            <feather-icon icon="TrashIcon"></feather-icon>
          </b-button>
        </div>
      </div>
      <div v-if="playlist.description" class="text-muted small mt-1">
        {{ playlist.description }}
      </div>
    </template>

    <!-- Items Container -->
    <div class="playlist-items">
      <div v-if="items.length === 0" class="text-center py-4 text-muted">
        <i class="fas fa-inbox" style="font-size: 2rem;"></i>
        <p class="mt-2 mb-0">هیچ رسانه‌ای در این پلی‌لیست وجود ندارد.</p>
      </div>

      <div v-else class="items-list">
        <PlaylistItem
            v-for="(item, index) in items"
            :key="item.id"
            :item="item"
            :index="index"
            :playlist-id="playlist.id"
            @remove="handleRemoveItem"
            @drag-start="dragStart"
            @drag-end="dragEnd"
            @drag-over="dragOver"
            @drop="handleDrop"
        />
      </div>
    </div>

    <!-- Footer with reorder hint -->
    <template #footer>
      <div class="d-flex justify-content-between align-items-center flex-wrap">
<!--        <small class="text-muted">-->
<!--          <i class="fas fa-grip-vertical"></i>-->
<!--          برای مرتب‌سازی، آیتم‌ها را بکشید و رها کنید-->
<!--        </small>-->
        <span class="text-muted small">
          آخرین بروزرسانی: {{ formatDate(playlist.updatedAt || playlist.createdAt) }}
        </span>
      </div>
    </template>
  </b-card>
</template>

<script>
import { BCard, BButton } from 'bootstrap-vue'
import { PlaylistRemoveItemRequest, PlaylistReorderItemsRequest } from '@/libs/Api/Playlist'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import PlaylistItem from './PlaylistItem.vue'

export default {
  name: 'PlaylistCard',
  components: { BCard, BButton, PlaylistItem },
  props: {
    playlist: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      items: [],
      loading: false,
      draggedIndex: null,
      draggedItemId: null
    }
  },
  created() {
    this.items = this.playlist.items || []
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      return d.toLocaleDateString('fa-IR') + ' ' + d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
    },
    handleRemoveItem({ playlistId, fileEntryId }) {
      this.$bvModal.msgBoxConfirm(
          'آیا از حذف این رسانه از پلی‌لیست اطمینان دارید؟',
          {
            title: 'تأیید حذف',
            okVariant: 'danger',
            okTitle: 'حذف',
            cancelTitle: 'لغو'
          }
      ).then(confirmed => {
        if (confirmed) {
          this.removeItem(playlistId, fileEntryId)
        }
      })
    },
    removeItem(playlistId, fileEntryId) {
      this.loading = true
      const request = new PlaylistRemoveItemRequest(this)
      request.setParams({ playlistId, fileEntryId })
      request.fetch(
          () => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'موفق',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'رسانه از پلی‌لیست حذف شد.'
              }
            })
            // Remove from local items
            this.items = this.items.filter(item => item.fileEntryId !== fileEntryId)
            this.$emit('refresh')
          },
          (error) => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'خطا',
                icon: 'XIcon',
                variant: 'danger',
                text: error.message || 'مشکل در حذف رسانه'
              }
            })
          }
      )
    },
    // Drag & Drop handlers
    dragStart(event, index, itemId) {
      this.draggedIndex = index
      this.draggedItemId = itemId
      event.dataTransfer.effectAllowed = 'move'
    },
    dragEnd() {
      this.draggedIndex = null
      this.draggedItemId = null
    },
    dragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    handleDrop(event, targetIndex, targetItemId) {
      event.preventDefault()
      if (this.draggedIndex === null || this.draggedItemId === targetItemId) return

      const fromIndex = this.draggedIndex
      const toIndex = targetIndex

      // Reorder locally
      const reordered = [...this.items]
      const [movedItem] = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, movedItem)
      this.items = reordered

      // Build order map
      const order = {}
      this.items.forEach((item, idx) => {
        order[item.fileEntryId] = idx
      })

      // Send reorder request
      this.loading = true
      const request = new PlaylistReorderItemsRequest(this)
      request.setParams({
        playlistId: this.playlist.id,
        order: order
      })
      request.fetch(
          () => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'موفق',
                icon: 'CheckIcon',
                variant: 'success',
                text: 'ترتیب رسانه‌ها بروزرسانی شد.'
              }
            })
            this.$emit('reorder', { playlistId: this.playlist.id, order })
          },
          (error) => {
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              position: 'bottom-center',
              props: {
                title: 'خطا',
                icon: 'XIcon',
                variant: 'danger',
                text: error.message || 'مشکل در بروزرسانی ترتیب'
              }
            })
            // Revert to original order
            this.items = this.playlist.items || []
          }
      )
    }
  }
}
</script>

<style scoped>
.playlist-card {
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}
.playlist-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}
.playlist-card >>> .card-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 1.5rem;
}
.playlist-card >>> .card-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
}
.playlist-status {
  font-size: 11px;
  padding: 2px 12px;
  border-radius: 30px;
  font-weight: 600;
}
.playlist-status.active {
  background: #dcfce7;
  color: #166534;
}
.playlist-status.inactive {
  background: #fef3c7;
  color: #92400e;
}
.playlist-count {
  font-size: 13px;
  color: #64748b;
}
.playlist-items {
  padding: 0.5rem 0;
  min-height: 80px;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}
</style>
